import { LoadingOutlined } from "@ant-design/icons";
import { Col, Spin, Table, Typography } from "antd";
import { Paging } from "api/baseApiResponseModel/BaseApiResponseModel";
import { isNull, isNumber, isString } from "lodash";
import { PureComponent, ReactNode } from "react";
import { colorFormat } from "utils/format/ColorFormat";
import { NumberFormat } from "utils/format/NumberFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import {
  ITableComponentState,
  TableComponentModel,
} from "./model/TableComponentModel";
import { LocalContext } from "context/AuthenticationContext";

const { Text } = Typography;

export default class TableComponent<T extends Object> extends PureComponent<
  TableComponentModel<T>,
  ITableComponentState
> {
  static contextType = LocalContext;
  context: React.ContextType<typeof LocalContext>;

  constructor(props: TableComponentModel<T>) {
    super(props);
    this.state = {
      detailPage: 0,
      detailPageSize: 10,
    };
    this.handleDetailTableChange = this.handleDetailTableChange.bind(this);
    this.handleTotal = this.handleTotal.bind(this);
  }

  private handleDetailTableChange = async (pagination?: Paging | any) => {
    this.setState({
      detailPage: pagination?.current! - 1,
      detailPageSize: pagination?.pageSize,
    });
  };

  private handleTotal = (total: number) => {
    const { summaryInfo } = this.props?.data;

    return (
      <div className="absolute left-0 flex flex-wrap w-1/2 xl:gap-x-8 gap-x-3 font-medium overflow-auto text-ellipsis whitespace-nowrap">
        <div className="flex items-center lg:text-base text-xs min-w-0">
          {this.props?.data?.totalTitle}:
          <Text
            style={{ color: colorFormat?.Blue }}
            className="ml-2 lg:text-base text-xs truncate"
          >
            {total}
          </Text>
        </div>

        {summaryInfo && (
          <>
            <div className="flex items-center lg:text-base text-xs min-w-0">
              {strings.GlobalTotalSummary.Success}:
              <Text
                style={{ color: colorFormat?.Green }}
                className="ml-2 lg:text-base text-xs truncate"
              >
                {summaryInfo?.success ?? 0}
              </Text>
            </div>

            <div className="flex items-center lg:text-base text-xs min-w-0">
              {strings.GlobalTotalSummary.Error}:
              <Text
                style={{ color: "#E22A2A" }}
                className="ml-2 lg:text-base text-xs truncate"
              >
                {summaryInfo?.error ?? 0}
              </Text>
            </div>

            <div className="flex items-center lg:text-base text-xs min-w-0">
              {strings.GlobalTotalSummary.Other}:
              <Text
                style={{ color: colorFormat?.Black }}
                className="ml-2 lg:text-base text-xs truncate"
              >
                {summaryInfo?.other ?? 0}
              </Text>
            </div>

            <div className="flex items-center lg:text-base text-xs min-w-0">
              {strings.GlobalTotalSummary.Revenue}:
              <Text
                style={{ color: colorFormat?.Green }}
                className="ml-2 lg:text-base text-xs truncate"
              >
                {`${NumberFormat.currencyFormat(summaryInfo?.revenue ?? 0, this?.context?.user?.currency)}`}
              </Text>
            </div>
          </>
        )}
      </div>
    );
  };

  private clickToCopy = async (value: unknown, key: string) => {
    let valueRendered = isNumber(value)
      ? value?.toFixed(2)
      : isString(value) && isNumber(parseFloat(value))
        ? String(parseFloat(value).toFixed(2))
        : String(value);
    await window?.navigator?.clipboard
      ?.writeText(
        key?.toLowerCase()?.includes("price")
          ? valueRendered
          : (value as string)
      )
      .then(() =>
        getMessage(
          `${strings.FileManagement.Placeholder.SavedValue}: ${key?.toLowerCase()?.includes("price")
            ? valueRendered
            : (value as string)
          }`,
          4,
          "success"
        )
      );
  };

  render(): ReactNode {
    const {
      forDetail,
      page,
      pageSize,
      columns,
      dataSource,
      scroll,
      rowKey,
      loading,
      loadingTitle,
      total,
      handleTableChange,
    } = this.props.data;
    const { detailPage, detailPageSize } = this.state;

    return (
      <Col span={24} className="mt-1">
        {forDetail === true ? (
          <Table
            columns={[
              {
                title: strings.GlobalLabels.STT,
                width: "4%",
                render: (item, record, index) => (
                  <span className="font-semibold">
                    {page * pageSize + index + 1}
                  </span>
                ),
                align: "center",
              },
              ...columns?.map((child) => ({
                ...child,
                onCell: (record) => {
                  const isUnCopyable = [
                    "status",
                    "type",
                    "simtype",
                    "createdAt",
                    "updatedAt",
                  ]?.some((text) =>
                    child["dataIndex"]?.toLowerCase()?.includes(text)
                  );
                  return {
                    onClick: async () => {
                      if (
                        !isUnCopyable &&
                        !!child["dataIndex"] &&
                        !!record[child["dataIndex"]]
                      ) {
                        await this.clickToCopy(
                          record[child["dataIndex"]],
                          child["dataIndex"]
                        );
                      }
                    },
                  };
                },
              })),
            ]}
            size="middle"
            showSorterTooltip={false}
            dataSource={dataSource}
            onChange={this.handleDetailTableChange}
            scroll={{
              x: "max-content",
              y: (scroll && (scroll as any).y) || 520,
            }}
            rowKey={!!rowKey ? rowKey : null}
            className="h-full relative"
            pagination={{
              pageSize: detailPageSize,
              current: detailPage + 1,
              className: "w-1/2",
              total: dataSource?.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: this.handleTotal,
            }}
          />
        ) : (
          <Spin
            spinning={loading}
            indicator={
              <LoadingOutlined className="text-blue-600 font-semibold" />
            }
            tip={
              <span className="font-semibold text-blue-600">
                {loadingTitle}
              </span>
            }
          >
            <Table
              columns={[
                {
                  title: strings.GlobalLabels.STT,
                  width: !isNull(scroll.x) ? "4%" : 80,
                  render: (item, record, index) => (
                    <span className="font-semibold">
                      {page * pageSize + index + 1}
                    </span>
                  ),
                  align: "center",
                },
                ...columns?.map((child) => ({
                  ...child,
                  onCell: (record) => {
                    const isUnCopyable = [
                      "status",
                      "type",
                      "simtype",
                      "createdAt",
                      "updatedAt",
                    ]?.some((text) =>
                      child["dataIndex"]?.toLowerCase()?.includes(text)
                    );
                    return {
                      onClick: async () => {
                        if (
                          !isUnCopyable &&
                          !!child["dataIndex"] &&
                          !!record[child["dataIndex"]]
                        ) {
                          await this.clickToCopy(
                            record[child["dataIndex"]],
                            child["dataIndex"]
                          );
                        }
                      },
                    };
                  },
                })),
              ]}
              rowKey={rowKey}
              size="middle"
              showSorterTooltip={false}
              dataSource={dataSource}
              onChange={handleTableChange}
              scroll={{
                x: !isNull(scroll.x) ? scroll.x : "max-content",
                y: (scroll && (scroll as any).y) || 520,
              }}
              className="h-full"
              pagination={{
                pageSize: pageSize,
                current: page + 1,
                total: total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: this.handleTotal,
              }}
            />
          </Spin>
        )}
      </Col>
    );
  }
}
